import { createSelector } from 'reselect';
import { RootState } from '../rootReducer';
import { ContactsState } from '../../types/contacts/contacts.types';

// Base selector
const selectContactsState = (state: RootState): ContactsState => state.contacts;

// Memoized selectors
export const selectContacts = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contacts
);

export const selectSelectedContact = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.selectedContact
);

export const selectContactsLoading = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.loading
);

export const selectContactsError = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.error
);

export const selectContactsPagination = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.pagination
);

// Derived selectors
export const selectContactById = (id: string) =>
  createSelector([selectContacts], (contacts) =>
    contacts.find((contact) => contact._id === id || contact.custom_id === id)
  );

export const selectContactsTotal = createSelector(
  [selectContactsPagination],
  (pagination) => pagination.total
);

export const selectContactsCurrentPage = createSelector(
  [selectContactsPagination],
  (pagination) => pagination.page
);

export const selectContactsTotalPages = createSelector(
  [selectContactsPagination],
  (pagination) => pagination.totalPages
);

export const selectContactsSuccess = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.success
);

export const selectContactsExportBlob = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.exportBlob
);

export const selectDeleteAllContactsSuccess = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.deleteAllContactsSuccess
);

export const selectBulkDeleteContactsSuccess = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.bulkDeleteContactsSuccess
);

// Contact Segments selectors
export const selectContactSegments = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactSegments
);

export const selectContactSegmentsLoading = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactSegmentsLoading
);

export const selectContactSegmentsError = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactSegmentsError
);

export const selectContactSegmentsPagination = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactSegmentsPagination
);

export const selectContactSegmentsTotal = createSelector(
  [selectContactSegmentsPagination],
  (pagination) => pagination.total
);

export const selectContactSegmentsCurrentPage = createSelector(
  [selectContactSegmentsPagination],
  (pagination) => pagination.page
);

export const selectContactSegmentsTotalPages = createSelector(
  [selectContactSegmentsPagination],
  (pagination) => pagination.totalPages
);

// Contact Activity selectors
export const selectContactActivity = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactActivity
);

export const selectContactActivities = createSelector(
  [selectContactActivity],
  (contactActivity) => contactActivity.activities
);

export const selectContactActivityLoading = createSelector(
  [selectContactActivity],
  (contactActivity) => contactActivity.loading
);

export const selectContactActivityError = createSelector(
  [selectContactActivity],
  (contactActivity) => contactActivity.error
);

export const selectContactActivityTotal = createSelector(
  [selectContactActivity],
  (contactActivity) => contactActivity.activities.length
);

export const selectBulkImportContacts = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.bulkImportContacts
);

// Contact Emails selectors
export const selectContactEmails = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactEmails
);

export const selectContactEmailsLoading = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactEmailsLoading
);

export const selectContactEmailsError = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactEmailsError
);

export const selectContactEmailsPagination = createSelector(
  [selectContactsState],
  (contactsState) => contactsState.contactEmailsPagination
);

export const selectContactEmailsTotal = createSelector(
  [selectContactEmailsPagination],
  (pagination) => pagination.total
);

export const selectContactEmailsCurrentPage = createSelector(
  [selectContactEmailsPagination],
  (pagination) => pagination.page
);

export const selectContactEmailsTotalPages = createSelector(
  [selectContactEmailsPagination],
  (pagination) => pagination.totalPages
);
